import type { EcoleSchool } from "@shared/ecole.types";
import { Check } from "lucide-react";
import { useId, useState } from "react";
import { Button } from "@/components/ds/Button";
import { EditableField, useFieldSave } from "@/components/ds/EditableField";
import { brandToCssVars } from "@/lib/brand";
import { hexToHslTriplet, hslToRgb, parseHsl, toHex } from "@/lib/color";

/**
 * Le choix de l'aplat de navigation, avec ce qu'il produit sous les yeux.
 *
 * Ce n'est pas un champ de couleur ordinaire, et la raison est la même que celle
 * qui a fait exister `navBase` : les cinq valeurs de la barre se DÉRIVENT de cette
 * seule couleur, y compris la couleur du texte, qui se calcule par luminance. Une
 * école ne peut donc pas se rendre illisible — mais elle ne peut pas non plus
 * devenir ce qu'elle a choisi si on ne le lui montre pas.
 *
 * L'aperçu est donc un vrai fragment de barre, monté avec les vraies variables :
 * ce qui s'affiche ici est exactement ce que l'entreprise verra.
 */

/**
 * Un point de départ, pas une palette : l'école arrive avec sa couleur, pas avec la nôtre.
 *
 * Chaque teinte porte un NOM : « Couleur #101c2c » ne disait rien à un lecteur d'écran.
 */
const SUGGESTIONS = [
	{ hex: "#101c2c", nom: "Bleu nuit" },
	{ hex: "#1e3a5f", nom: "Bleu marine" },
	{ hex: "#22543d", nom: "Vert sapin" },
	{ hex: "#4a2545", nom: "Prune" },
	{ hex: "#3f3d56", nom: "Gris ardoise" },
	{ hex: "#7c2d12", nom: "Brique" },
];

export function NavColorField({
	school,
	onSave,
}: {
	school: EcoleSchool;
	onSave: (navBase: string | null) => Promise<boolean>;
}) {
	// `useId` par principe : un identifiant en dur dans un composant est un pari sur
	// le fait qu'il ne sera jamais monté deux fois. Ici il ne l'est pas (`ConfigPage`
	// porte une `key`), donc c'est une précaution, pas un défaut corrigé.
	const champId = useId();
	const courante = school.navBase ?? school.accent;
	const parsee = parseHsl(courante);
	const hexCourant = parsee ? toHex(hslToRgb(...parsee)) : "#101c2c";

	const field = useFieldSave(school.navBase ?? "", async (triplet) =>
		onSave(triplet === "" ? null : triplet),
	);
	// L'aperçu suit la MAIN, pas l'enregistrement : le sélecteur du navigateur émet
	// pendant qu'on fait glisser, et attendre le relâchement rendrait le choix aveugle.
	const [survol, setSurvol] = useState<string | null>(null);
	const apercu = survol ?? field.value ?? "";

	const marque = { ...school, navBase: apercu || null };

	return (
		<EditableField
			htmlFor={champId}
			label="La couleur de votre espace"
			hint="Les textes s'adaptent pour rester lisibles."
			state={field.state}
		>
			<div className="flex flex-col gap-3">
				<div className="flex flex-wrap items-center gap-2">
					{/*
					 * L'anneau de focus est porté par le CARRÉ, pas par le champ.
					 *
					 * Le champ de couleur est transparent et couvre le carré : c'est lui
					 * qui reçoit le focus au clavier, mais son anneau natif se dessine sur
					 * du vide et ne se voit pas. On le remonte donc sur ce qui est visible.
					 * `:focus-visible` et pas `:focus-within` : à la souris, le carré ne
					 * doit rien changer, c'est la règle des champs du référentiel.
					 */}
					<label
						htmlFor={champId}
						className="relative inline-flex size-11 shrink-0 md:size-10 cursor-pointer items-center justify-center overflow-hidden rounded-field border border-mg-border has-focus-visible:outline-solid has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-mg-border-focus"
					>
						<span
							className="absolute inset-0"
							style={{ backgroundColor: apercu ? `hsl(${apercu})` : hexCourant }}
						/>
						<input
							id={champId}
							type="color"
							// Nommé par le libellé du champ (`htmlFor`), et décrit par l'aide.
							aria-describedby={`${champId}-aide`}
							className="absolute inset-0 cursor-pointer opacity-0"
							value={apercu ? toHex(hslToRgb(...(parseHsl(apercu) ?? [0, 0, 0]))) : hexCourant}
							onChange={(event) => setSurvol(hexToHslTriplet(event.target.value) ?? null)}
							onBlur={() => {
								if (!survol) return;
								field.setValue(survol);
								void field.commit(survol);
								setSurvol(null);
							}}
						/>
					</label>

					{SUGGESTIONS.map(({ hex, nom }) => {
						const triplet = hexToHslTriplet(hex) ?? "";
						const choisie = apercu === triplet;
						return (
							<button
								key={hex}
								type="button"
								aria-label={nom}
								aria-pressed={choisie}
								onClick={() => {
									setSurvol(null);
									field.setValue(triplet);
									void field.commit(triplet);
								}}
								className={`grid size-11 place-items-center md:size-8 rounded-field border transition-transform hover:scale-105 ${
									choisie ? "border-mg-heading" : "border-mg-border-subtle"
								}`}
								style={{ backgroundColor: hex }}
							>
								{/* Le bouton porte un `aria-label`, qui REMPLACE son contenu dans le
								    nom accessible : ce coche n'était déjà annoncé nulle part.
								    `aria-hidden` ne corrige donc rien, il rend l'intention explicite. */}
								{choisie ? (
									<Check
										className="size-4 text-mg-inverse mix-blend-difference"
										aria-hidden="true"
									/>
								) : null}
							</button>
						);
					})}

					{school.navBase ? (
						<Button
							size="sm"
							onClick={() => {
								setSurvol(null);
								field.setValue("");
								void field.commit("");
							}}
						>
							Revenir à la couleur Mentor Goal
						</Button>
					) : null}
				</div>

				{/* L'aperçu monte les VRAIES variables : ce fragment est la barre. */}
				<div
					style={brandToCssVars(marque)}
					className="overflow-hidden rounded-card border border-mg-border"
				>
					<div className="bg-mg-nav p-3 text-mg-nav-fg">
						<p className="t-label">{school.schoolName}</p>
						<p className="t-caption text-mg-nav-fg-muted">Espace partenaire</p>
						<div className="mt-3 flex flex-col gap-1">
							<span className="t-label rounded-field bg-mg-nav-selected px-2.5 py-1.5">
								Mes offres
							</span>
							<span className="t-label rounded-field bg-mg-nav-hover px-2.5 py-1.5 text-mg-nav-fg-muted">
								Profils reçus
							</span>
						</div>
					</div>
				</div>
			</div>
		</EditableField>
	);
}
